import { Dispatch, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { isEmpty } from 'lodash';
import { setCurrency } from 'common/actions/format';
import { FINANCIALS_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { MEASUREMENT_DATE_FILTER } from 'common/constants/pageFilters';
import formatReducer from 'common/reducers/format/reducer';
import { useStore } from 'common/store';
import { CurrentCompanyMeasurementDate } from 'common/types/measurementDate';
import { DEFAULT_CURRENCY, THOUSANDS } from 'components/FeaturedSpreadsheet/constants';
import LayoutContext from 'context/LayoutContext';
import { getCurrency, worldCurrencies } from 'utilities';
import { CurrencyCode } from '../types/format';

export interface AdditionalCurrency {
  currency: string;
  currencyType: string;
  currencyName: string;
}

interface ProcessedAdditionalCurrency {
  sourceCurrency: string;
  currencyType: string;
  currencyName: string;
  symbol: string;
  exchangeRate: number | undefined;
}

interface UseFormatProps {
  page?: string;
  sourceCurrency?: CurrencyCode;
  units?: string;
  additionalCurrencies?: AdditionalCurrency[];
  marketDate?: string;
  currencyName?: string;
  measurementDate?: CurrentCompanyMeasurementDate | null;
}

interface PageFilter {
  id?: string;
  selectedValue?: number;
}

function getAdditionalCurrencies(
  additionalCurrencies?: AdditionalCurrency[]
): ProcessedAdditionalCurrency[] | undefined {
  return additionalCurrencies?.map(additionalCurrency => ({
    sourceCurrency: additionalCurrency.currency,
    currencyType: additionalCurrency.currencyType,
    currencyName: additionalCurrency.currencyName,
    symbol: (worldCurrencies as Record<string, string>)[additionalCurrency.currency] || '',
    exchangeRate: undefined,
  }));
}

const useFormat = (props?: UseFormatProps): [any, Dispatch<any>] => {
  const {
    page = FINANCIALS_CURRENCY_PAGE,
    sourceCurrency,
    units,
    additionalCurrencies,
    marketDate,
    currencyName,
    measurementDate,
  } = props || {};

  const [{ companyInfo }] = useStore();
  const { pageFilters } = useContext(LayoutContext);

  const selectedCurrency: CurrencyCode = sourceCurrency ?? DEFAULT_CURRENCY;

  const measurementDateId = useMemo((): number | null => {
    if (measurementDate?.id) {
      return measurementDate.id;
    }
    if (pageFilters) {
      const selectedMD = pageFilters.find((filter: PageFilter) => filter.id?.includes(MEASUREMENT_DATE_FILTER));

      return selectedMD?.selectedValue ?? null;
    }

    return null;
  }, [pageFilters, measurementDate]);

  const [format, dispatch] = useReducer(formatReducer, {
    currency: {
      code: selectedCurrency,
      sourceCurrency: selectedCurrency,
      exchangeRate: undefined,
      currencyName,
      additionalCurrencies: getAdditionalCurrencies(additionalCurrencies),
      symbol: (worldCurrencies as Record<string, string>)[selectedCurrency.toString()] || '',
      units: units ?? THOUSANDS,
      measurementDateId,
      marketDate,
    },
  });

  // Last source currency this hook seeded; lets the effect below tell a real
  // source change apart from unrelated dependency churn.
  const seededSourceCurrency = useRef<CurrencyCode | null>(null);

  useEffect(() => {
    // Set current Measurement Date to currency
    if (measurementDateId) {
      dispatch(
        setCurrency({
          measurementDateId,
        }) as any
      );
    }

    if (!isEmpty(sourceCurrency)) {
      if (seededSourceCurrency.current !== sourceCurrency) {
        // Source currency changed (mount or company switch): seed the view
        // currency from it.
        seededSourceCurrency.current = sourceCurrency as CurrencyCode;
        dispatch(
          setCurrency({
            code: sourceCurrency,
            sourceCurrency,
            exchangeRate: undefined,
            marketDate,
            additionalCurrencies: getAdditionalCurrencies(additionalCurrencies),
            symbol: (worldCurrencies as Record<string, string>)[sourceCurrency as string] || '',
          }) as any
        );
      } else {
        // Same source: keep marketDate/additionalCurrencies fresh without
        // clobbering a user-picked view currency.
        dispatch(
          setCurrency({
            marketDate,
            additionalCurrencies: getAdditionalCurrencies(additionalCurrencies),
          }) as any
        );
      }
      // If no Source Currency, seed from the Company currency
    } else if (!isEmpty(companyInfo)) {
      const code = getCurrency({ companyInfo, page }) as CurrencyCode;

      if (code && seededSourceCurrency.current !== code) {
        seededSourceCurrency.current = code;
        dispatch(
          setCurrency({
            code,
            sourceCurrency: code,
            symbol: (worldCurrencies as Record<string, string>)[code.toString()] || '',
          }) as any
        );
      }
    }
  }, [companyInfo, page, measurementDateId, sourceCurrency, marketDate, additionalCurrencies]);

  return [format, dispatch];
};

export default useFormat;
